import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  RegistrationFormInterface,
  RegistrationState,
  Department,
  City,
  ResponseRegistrationInterface,
  FormDataErrorInterface
} from '../../domain';
import { getCandidateConfig } from '../../../../../config/candidates';

const candidateName = process.env.REACT_APP_CANDIDATE_NAME || "default";
const candidateConfig = getCandidateConfig(candidateName);

const initialState: RegistrationState = {
  formData: {
    name: '',
    email: '',
    phoneShow: '',
    phone: '',
    ageRange: candidateConfig.ageRanges[0]?.id || '18-25',
    department: { id: 0, name: '' },
    city: { id: 0, name: '' },
    policyAccepted: false,
    gender: 'male',
    codeCountry: '',
    topicInterest: candidateConfig.topics.length > 0 ? '1' : ''
  },
  formDataError: {
    name: false,
    email: false,
    phone: false,
    identification: false,
    birthdate: false,
    department: false,
    city: false,
    policyAccepted: false
  },
  departments: [],
  cities: [],
  loadingDepartments: false,
  loadingCities: false,
  loading: false,
  error: null,
  success: false,
  responseRegistration: {
    message: '',
    success: false,
    data: null
  },
  ageRanges: candidateConfig.ageRanges,
  genders: [
    { id: 'male', name: 'Masculino' },
    { id: 'female', name: 'Femenino' },
  ],
  topicsInterest: candidateConfig.topics,
};

/**
 * Thunk para buscar todos los departamentos de Colombia.
 */
export const fetchDepartmentsThunk = createAsyncThunk<Department[]>(
  'location/fetchDepartments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/Department?sortBy=name&sortDirection=asc`);
      if (!response.ok) {
        throw new Error('No se pudieron obtener los departamentos.');
      }
      const data: Department[] = await response.json();
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Thunk para buscar las ciudades de un departamento específico.
 * @param {number} departmentId - El ID del departamento.
 */
export const fetchCitiesByDepartmentThunk = createAsyncThunk<City[], number>(
  'location/fetchCitiesByDepartment',
  async (departmentId, { rejectWithValue }) => {
    console.log(departmentId)
    try {
      if (departmentId === 0) {
        return [{ id: 0, name: 'Extranjero' }] as City[];
      }
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/Department/${departmentId}/cities?sortBy=name&sortDirection=asc`);
      if (!response.ok) {
        throw new Error('No se pudieron obtener las ciudades.');
      }
      const data: City[] = await response.json();
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const RegistrationSlice = createSlice({
  name: 'registration',
  initialState,
  reducers: {
    setFormDataReducer: (state, action: PayloadAction<Partial<RegistrationFormInterface>>) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    setFormDataErrorReducer: (state, action: PayloadAction<Partial<FormDataErrorInterface>>) => {
      state.formDataError = { ...state.formDataError, ...action.payload };
    },
    submitRegistrationStartReducer: (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    },
    submitRegistrationSuccessReducer: (state, action: PayloadAction<ResponseRegistrationInterface>) => {
      state.loading = false;
      state.success = true;
      state.error = null;
      state.formData = initialState.formData;
      state.responseRegistration = action.payload;
    },
    submitRegistrationFailureReducer: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    },
    clearRegistrationErrorReducer: (state) => {
      state.error = null;
      state.success = false;
    },
    resetRegistrationStateReducer: (state) => {
      return initialState
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDepartmentsThunk.pending, (state) => {
        state.loadingDepartments = true;
        state.error = null;
      })
      .addCase(fetchDepartmentsThunk.fulfilled, (state, action: PayloadAction<Department[]>) => {
        state.loadingDepartments = false;
        state.departments = action.payload;
        state.departments.push({ id: 0, name: 'Extranjero' });
        
        const campaignScope = process.env.REACT_APP_CAMPAIGN_SCOPE || "NACIONAL";
        const campaignDeptName = process.env.REACT_APP_CAMPAIGN_DEPARTMENT || "";

        if (campaignScope === "REGIONAL" && campaignDeptName) {
            const targetDept = state.departments.find(d => d.name.trim().toLowerCase() === campaignDeptName.trim().toLowerCase());
            if (targetDept) {
                state.formData.department = { id: targetDept.id, name: targetDept.name };
            } else {
                 state.formData.department = { id: action.payload[0]?.id || 0, name: action.payload[0]?.name || '' };
            }
        } else {
             state.formData.department = { id: action.payload[0]?.id || 0, name: action.payload[0]?.name || '' };
        }
      })
      .addCase(fetchDepartmentsThunk.rejected, (state, action) => {
        state.loadingDepartments = false;
        state.error = action.payload as string;
      })
      .addCase(fetchCitiesByDepartmentThunk.pending, (state) => {
        state.loadingCities = true;
        state.error = null;
      })
      .addCase(fetchCitiesByDepartmentThunk.fulfilled, (state, action: PayloadAction<City[]>) => {
        state.loadingCities = false;
        state.cities = action.payload;
        state.formData.city = { id: action.payload[0]?.id || 0, name: action.payload[0]?.name || '' };
      })
      .addCase(fetchCitiesByDepartmentThunk.rejected, (state, action) => {
        state.loadingCities = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setFormDataReducer,
  submitRegistrationStartReducer,
  submitRegistrationSuccessReducer,
  submitRegistrationFailureReducer,
  clearRegistrationErrorReducer,
  resetRegistrationStateReducer,
  setFormDataErrorReducer
} = RegistrationSlice.actions;

export default RegistrationSlice.reducer;
